import { InsuranceStatus } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  Button,
  ContentLoader,
  ModalControlResponse,
  PopoverMenu,
  PopoverMenuItem,
  useModalControl,
  usePopoverMenu,
} from '@frontend/design-system';
import { useOpenInsuranceReport } from '../hooks/use-open-insurance-report';
import { insuranceTabTrackingIds } from '../tracking-ids';
import { OverrideStatusSidePanel } from './override-status-side-panel';

interface Props {
  verificationStatus?: InsuranceStatus;
  requestId?: string;
  shouldShowWriteBack: boolean;
  verifyInsuranceModalControl: ModalControlResponse;
  onVerificationMetricClick: () => void;
  writeBackModal: ModalControlResponse;
  shouldShowTuuthFairyReport: boolean;
}

export const ActionsMenu = ({
  requestId,
  verificationStatus,
  shouldShowWriteBack,
  verifyInsuranceModalControl,
  onVerificationMetricClick,
  writeBackModal,
  shouldShowTuuthFairyReport,
}: Props) => {
  const { t } = useTranslation('insuranceDetails');
  const { handleOpenInsuranceReport, isLoading } = useOpenInsuranceReport({ requestId, shouldShowTuuthFairyReport });

  const popoverMenuProps = usePopoverMenu();
  const overrideStatusModalControl = useModalControl();

  const handleVerificationMetricClick = () => {
    onVerificationMetricClick();
    popoverMenuProps.close();
  };
  const handleOverrideInsuranceStatusClick = () => {
    overrideStatusModalControl.openModal();
    popoverMenuProps.close();
  };
  const handleVerifyInsuranceClick = () => {
    if (writeBackModal.modalProps.show) {
      writeBackModal.closeModal();
    }
    if (verifyInsuranceModalControl) {
      verifyInsuranceModalControl.openModal();
    }
    popoverMenuProps.close();
  };
  const handleWritebackStatusClick = () => {
    if (verifyInsuranceModalControl.modalProps.show) {
      verifyInsuranceModalControl.closeModal();
    }
    if (writeBackModal) {
      writeBackModal.openModal();
    }
    popoverMenuProps.close();
  };

  const handleViewReportClick = () => {
    popoverMenuProps.close();
    handleOpenInsuranceReport();
  };

  const shouldShowViewReportMenu =
    verificationStatus?.toUpperCase() === InsuranceStatus.VERIFIED ||
    verificationStatus?.toUpperCase() === InsuranceStatus.ACTIVE ||
    verificationStatus?.toUpperCase() === InsuranceStatus.INACTIVE; // View Report is shown for Inactive as well if third party data is present

  return (
    <>
      <ContentLoader show={isLoading} />
      <Button
        trackingId={insuranceTabTrackingIds.actionMenuButton}
        variant='tertiary'
        iconName='more'
        {...popoverMenuProps.getTriggerProps()}
        label='more'
        size='large'
      />

      <PopoverMenu {...popoverMenuProps.getMenuProps()} title=''>
        {shouldShowViewReportMenu && (
          <PopoverMenuItem
            trackingId={insuranceTabTrackingIds.actionMenuViewReportButton}
            {...popoverMenuProps.getItemProps({ index: 0 })}
            onClick={handleViewReportClick}
          >
            <Icon name='external-link-small' />
            {t('View Report')}
          </PopoverMenuItem>
        )}
        <PopoverMenuItem
          trackingId={insuranceTabTrackingIds.actionMenuVerifyInsuranceButton}
          {...popoverMenuProps.getItemProps({ index: 1 })}
          onClick={handleVerifyInsuranceClick}
        >
          <Icon name='shield-small' />
          {t('Verify Insurance')}
        </PopoverMenuItem>
        <PopoverMenuItem
          trackingId={insuranceTabTrackingIds.actionMenuVerificationMetricButton}
          {...popoverMenuProps.getItemProps({ index: 2 })}
          onClick={handleVerificationMetricClick}
        >
          <Icon name='analytics-small' />
          {t('Verification Data')}
        </PopoverMenuItem>
        {shouldShowWriteBack && (
          <PopoverMenuItem
            trackingId={insuranceTabTrackingIds.actionMenuWriteBackStatusButton}
            {...popoverMenuProps.getItemProps({ index: 3 })}
            onClick={handleWritebackStatusClick}
          >
            <Icon name='sync-small' />
            {t('Writeback Status')}
          </PopoverMenuItem>
        )}
        <PopoverMenuItem
          trackingId={insuranceTabTrackingIds.actionMenuOverrideInsuranceStatusButton}
          {...popoverMenuProps.getItemProps({ index: 4 })}
          onClick={handleOverrideInsuranceStatusClick}
        >
          <Icon name='edit-status-small' />
          {t('Override Insurance')}
        </PopoverMenuItem>
      </PopoverMenu>
      <OverrideStatusSidePanel {...overrideStatusModalControl.modalProps} verificationStatus={verificationStatus} />
    </>
  );
};
